import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Form, Alert, Pagination } from 'react-bootstrap';
import { FiUsers, FiSearch, FiFilter, FiMail, FiPhone, FiCalendar, FiUser, FiMapPin, FiGlobe, FiRefreshCw, FiEyeOff, FiInfo, FiArrowRight } from 'react-icons/fi';
import { DatePickerInput } from '@mantine/dates';
import { MantineProvider, rem } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { useParams, useNavigate } from 'react-router-dom';
import { useGlobal } from '../../Context/GlobalContext';
import { useWorkspace } from '../../Context/WorkspaceContext';
import { useLanguage } from '../../Context/LanguageContext';
import ExportDropdown from '../../Components/ExportDropdown';
import { AgentLeadsSkeleton } from '../../Components/SkeletonLoaders';
import PageHeader from '../../Components/PageHeader';
import LeadsSetupTour from '../../Components/LeadsSetupTour';
import dayjs from 'dayjs';
import './agent-leads.css';
import OutlinedSecondaryButton from '../../Components/Buttons/OutlinedSecondaryButton';
import PrimaryButton from '../../Components/Buttons/PrimaryButton';
import OutlinedDangerButton from '../../Components/Buttons/OutlinedDangerButton';
import OutlinedPrimaryButton from '../../Components/Buttons/OutlinedPrimaryButton';
import LinkButton from '../../Components/Buttons/LinkButton';

const AgentLeads = () => {
  const { id: chatbotId } = useParams();
  const navigate = useNavigate();
  const { apiUrl } = useGlobal();
  const { workspaceData } = useWorkspace();
  const { t } = useLanguage();

  // State management
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agentName, setAgentName] = useState('');

  // Filter states
  const [currentFilters, setCurrentFilters] = useState({
    startDate: null,
    endDate: null,
    searchTerm: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: null,
    endDate: null,
    searchTerm: '',
  });

  // Date range for filter
  const [dateRange, setDateRange] = useState([null, null]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10,
    has_next: false,
    has_prev: false
  });

  // API functions
  const fetchLeads = useCallback(async (page = 1, filters = null) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('brandvox_token');
      const queryParams = new URLSearchParams({
        chatbot_id: chatbotId,
        page: page.toString(),
        limit: '10'
      });

      // Use provided filters or current appliedFilters state
      const filtersToUse = filters !== null ? filters : appliedFilters;

      if (filtersToUse.startDate) {
        queryParams.append('start_date', filtersToUse.startDate);
      }
      if (filtersToUse.endDate) {
        queryParams.append('end_date', filtersToUse.endDate);
      }
      if (filtersToUse.searchTerm) {
        queryParams.append('search_term', filtersToUse.searchTerm);
      }

      const response = await fetch(`${apiUrl}/api/v1/chatbot/get-chatbot-leads?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform the new API response format to match the expected UI format
      const transformedLeads = (data.leads || []).map(lead => ({
        id: `${lead.chatbot_id}_${lead.session_id}`,
        full_name: lead.name,
        email: lead.email,
        phone: lead.phone,
        timestamp: lead.timestamp,
        location: {
          city: lead.city,
          country: lead.country
        },
        source: lead.message_source,
        lead_data: lead.lead_data
      }));

      // Transform pagination format to match UI expectations
      const transformedPagination = {
        current_page: data.current_page,
        total_pages: data.total_pages,
        total_items: data.total_count,
        items_per_page: 10,
        has_next: data.has_more,
        has_prev: data.current_page > 1
      };

      setLeads(transformedLeads);
      setPagination(transformedPagination);
      setCurrentPage(page);

    } catch (err) {
      setError(t('dashboard.agentleads.errorHeading'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotId, apiUrl]); // Remove appliedFilters from dependencies to prevent infinite loop

  // Fetch agent/chatbot name
  const fetchAgentName = useCallback(async () => {
    if (!chatbotId || !apiUrl) return;

    try {
      // First check if we have a cached name
      const cachedAgentNames = JSON.parse(localStorage.getItem('cached_agent_names') || '{}');

      if (cachedAgentNames[chatbotId]) {
        setAgentName(cachedAgentNames[chatbotId]);
      } else {
        // Fetch from API if not in cache
        const token = localStorage.getItem('brandvox_token');
        const response = await fetch(
          `${apiUrl}/api/v1/chatbot/get-chatbot?chatbot_id=${chatbotId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch chatbot data');
        }

        const data = await response.json();
        if (data && data.chatbot_name) {
          setAgentName(data.chatbot_name);

          // Cache the name for future use
          const updatedCache = { ...cachedAgentNames, [chatbotId]: data.chatbot_name };
          localStorage.setItem('cached_agent_names', JSON.stringify(updatedCache));
        }
      }
    } catch (err) {
      // Don't set error state, just use default empty name
    }
  }, [chatbotId, apiUrl]);

  // Effects
  useEffect(() => {
    if (chatbotId) {
      // Call functions directly with empty filters for initial load
      fetchLeads(1, {
        startDate: null,
        endDate: null,
        searchTerm: '',
      });

      // Fetch agent name
      fetchAgentName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotId]); // Only depend on chatbotId

  // Filter handlers
  const handleDateChange = (dates) => {
    setDateRange(dates);
    if (dates && dates.length === 2) {
      setCurrentFilters(prev => ({
        ...prev,
        startDate: dates[0] ? dayjs(dates[0]).startOf('day').toISOString() : null,
        endDate: dates[1] ? dayjs(dates[1]).endOf('day').toISOString() : null,
      }));
    } else {
      setCurrentFilters(prev => ({
        ...prev,
        startDate: null,
        endDate: null,
      }));
    }
  };

  const handleSearchChange = (e) => {
    setCurrentFilters(prev => ({
      ...prev,
      searchTerm: e.target.value
    }));
  };

  const applyFilters = () => {
    setAppliedFilters(currentFilters);
    setCurrentPage(1);
    fetchLeads(1, currentFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      startDate: null,
      endDate: null,
      searchTerm: '',
    };
    setCurrentFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setDateRange([null, null]);
    setCurrentPage(1);
    fetchLeads(1, emptyFilters);
  };

  // Filter visibility for mobile (matching AgentChats pattern)
  const [showFilters, setShowFilters] = useState(false);

  // Tour modal state
  const [showTour, setShowTour] = useState(false);

  // Check if tour should be shown on mount (only when no leads and first visit)
  useEffect(() => {
    const tourCompleted = localStorage.getItem(`leads_tour_completed_${chatbotId}`);

    // Show tour if: not completed before AND no leads exist AND data is loaded
    if (!tourCompleted && !loading && leads.length === 0 && !error) {
      // Small delay for better UX
      const timer = setTimeout(() => {
        setShowTour(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [chatbotId, loading, leads.length, error]);

  const handleTourComplete = () => {
    localStorage.setItem(`leads_tour_completed_${chatbotId}`, 'true');
    setShowTour(false);
  };

  const handleTourClose = () => {
    localStorage.setItem(`leads_tour_completed_${chatbotId}`, 'true');
    setShowTour(false);
  };

  const handleRestartTour = () => {
    setShowTour(true);
  };

  // Handle filter toggle with scroll position management
  const handleFilterToggle = () => {
    setShowFilters(!showFilters);

    // On mobile, scroll to top of filters when opening
    if (!showFilters && window.innerWidth <= 768) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    fetchLeads(page);

    // Scroll to top immediately on mobile with a slight delay
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 100);
    }
  };


  // Format date for display (now handles ISO timestamps like chats)
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Handle both ISO strings (new format) and Unix timestamps (legacy)
    if (typeof timestamp === 'string') {
      return dayjs(timestamp).format('MMM DD, YYYY HH:mm');  // ISO string
    } else {
      return dayjs.unix(timestamp).format('MMM DD, YYYY HH:mm');  // Unix timestamp (fallback)
    }
  };


  // Calculate min date based on workspace chat history limit
  const getMinDate = () => {
    if (!workspaceData || !workspaceData.workspace_bv_chat_history_day_limit) return null;

    const minDate = new Date();
    minDate.setDate(minDate.getDate() - workspaceData.workspace_bv_chat_history_day_limit);
    return minDate;
  };

  // Create page header component for reuse
  const pageHeaderComponent = (
    <PageHeader
      icon={<FiUsers />}
      title={t('dashboard.agentleads.pageTitle')}
      description={t('dashboard.agentleads.pageDescription')}
      hideMobile={true}
    />
  );

  // Render loading state
  if (loading) {
    return <AgentLeadsSkeleton pageHeader={pageHeaderComponent} />;
  }

  // Render error state
  if (error) {
    return (
      <Container className="leads-container mt-4">
        <Alert variant="danger">
          <Alert.Heading>{t('dashboard.agentleads.errorHeading')}</Alert.Heading>
          <p>{error}</p>
          <OutlinedDangerButton
            onClick={() => fetchLeads(currentPage)}>
            <FiUsers className="me-2" /> {t('dashboard.agentleads.retryButton')}
          </OutlinedDangerButton>
        </Alert>
      </Container>
    );
  }

  return (
    <MantineProvider>
      <DatesProvider settings={{ locale: 'en', firstDayOfWeek: 0, weekendDays: [0] }}>
        {/* Tour Modal */}
        <LeadsSetupTour
          show={showTour}
          onClose={handleTourClose}
          onComplete={handleTourComplete}
          chatbotId={chatbotId}
        />

        <div className="agent-leads-container">
          {pageHeaderComponent}

          <div className="leads-content">
            {/* Sidebar for filters */}
            <div className="leads-sidebar">
              <div className="sidebar-header-leads">
                <h5>{t('dashboard.agentleads.filtersSearchTitle')}</h5>
                {/* Mobile only toggle button */}
                <LinkButton
                  size="sm"
                  className="d-md-none"
                  onClick={handleFilterToggle}
                >
                  {showFilters ?
                    <><FiEyeOff className="me-1" /> {t('dashboard.agentleads.hideButton')}</> :
                    <><FiFilter className="me-1" /> {t('dashboard.agentleads.showButton')}</>
                  }
                </LinkButton>
              </div>

              {/* Filters section - Always visible on desktop, toggleable on mobile */}
              <div className={`sidebar-filters d-md-block ${showFilters ? 'd-block' : 'd-none'}`}>
                <div className="filter-section">
                  {/* Search Filter */}
                  <div className="filter-item mb-3">
                    <Form.Label className="filter-label">
                      {t('dashboard.agentleads.searchLeadsLabel')}
                    </Form.Label>
                    <div className="position-relative">
                      <FiSearch className="position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', zIndex: 2 }} />
                      <Form.Control
                        type="text"
                        placeholder={t('dashboard.agentleads.searchPlaceholder')}
                        value={currentFilters.searchTerm}
                        onChange={handleSearchChange}
                        className="form-select-custom"
                        style={{ paddingLeft: '40px' }}
                      />
                    </div>
                  </div>

                  {/* Date Range Filter */}
                  <div className="date-range-filters">
                    <div className="filter-item">
                      <Form.Label className="filter-label">
                        {t('dashboard.agentleads.dateRangeLabel')}
                      </Form.Label>
                      <div className="date-picker-container">
                        <DatePickerInput
                          type="range"
                          value={dateRange}
                          onChange={handleDateChange}
                          placeholder={t('dashboard.agentleads.dateRangePlaceholder')}
                          size="sm"
                          radius="md"
                          clearable
                          minDate={getMinDate()}
                          styles={{
                            input: {
                              fontSize: rem(14),
                              paddingTop: rem(8),
                              paddingBottom: rem(8),
                            },
                            day: {
                              borderRadius: '50%',
                            },
                            levelsGroup: {
                              borderRadius: rem(10),
                              boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)',
                              border: 'none',
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="filter-actions">
                    <OutlinedSecondaryButton
                      width="100%"
                      onClick={clearFilters}
                    >
                      <FiRefreshCw className="me-1" />
                      {t('dashboard.agentleads.clearButton')}
                    </OutlinedSecondaryButton>
                    <PrimaryButton
                      width="100%"
                      onClick={applyFilters}
                    >
                      <FiSearch className="me-1" />
                      {t('dashboard.agentleads.applyFiltersButton')}
                    </PrimaryButton>
                  </div>
                </div>
              </div>

            </div>

            {/* Main content */}
            <div className="leads-main-content">
              {leads.length > 0 ? (
                <div className="leads-data-container">
                  {/* Header actions */}
                  <div className="leads-data-header">
                    <div className="leads-data-title">
                      <h4 className="section-title-leads">
                        <FiUsers className="me-2" /> {t('dashboard.agentleads.leadsListTitle')}
                        {/* Mobile leads count next to title */}
                        <span className="d-md-none ms-2 leads-count-mobile">
                          {pagination.total_items}
                        </span>
                      </h4>
                      <div className="leads-count-info d-none d-md-block">
                        {t('dashboard.agentleads.showingText')} {((currentPage - 1) * pagination.items_per_page) + 1}
                        {' '}{t('dashboard.agentleads.toText')}{' '}
                        {Math.min(currentPage * pagination.items_per_page, pagination.total_items)}
                        {' '}{t('dashboard.agentleads.ofText')}{' '}{pagination.total_items} {t('dashboard.agentleads.resultsText')}
                      </div>
                    </div>
                    <div className="leads-data-actions">
                      <ExportDropdown
                        data={leads}
                        filename="leads"
                        appliedFilters={appliedFilters}
                        pagination={pagination}
                        isLoading={loading}
                        agentName={agentName}
                      />
                    </div>
                  </div>

                  {/* Table */}
                  <div className="table-responsive">
                    <Table hover className="leads-table">
                      <thead>
                        <tr>
                          <th><FiCalendar className="me-2" />{t('dashboard.agentleads.dateHeader')}</th>
                          <th><FiUser className="me-2" />{t('dashboard.agentleads.nameHeader')}</th>
                          <th><FiMail className="me-2" />{t('dashboard.agentleads.emailHeader')}</th>
                          <th><FiPhone className="me-2" />{t('dashboard.agentleads.phoneHeader')}</th>
                          <th><FiMapPin className="me-2" />{t('dashboard.agentleads.locationHeader')}</th>
                          <th><FiGlobe className="me-2" />{t('dashboard.agentleads.sourceHeader')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((lead, index) => (
                          <tr key={lead.id || index}>
                            <td data-label={t('dashboard.agentleads.dateHeader')}>
                              <span className="date-badge">
                                {formatDate(lead.timestamp)}
                              </span>
                            </td>
                            <td data-label={t('dashboard.agentleads.nameHeader')}>
                              <div className="lead-name">
                                <strong>{lead.full_name || t('dashboard.agentleads.notAvailable')}</strong>
                              </div>
                            </td>
                            <td data-label={t('dashboard.agentleads.emailHeader')}>
                              {lead.email ? (
                                <a href={`mailto:${lead.email}`} className="email-link">
                                  {lead.email}
                                </a>
                              ) : (
                                <span className="no-data">{t('dashboard.agentleads.notAvailable')}</span>
                              )}
                            </td>
                            <td data-label={t('dashboard.agentleads.phoneHeader')}>
                              {lead.phone ? (
                                <a href={`tel:${lead.phone}`} className="phone-link">
                                  {lead.phone}
                                </a>
                              ) : (
                                <span className="no-data">{t('dashboard.agentleads.notAvailable')}</span>
                              )}
                            </td>
                            <td data-label={t('dashboard.agentleads.locationHeader')}>
                              {lead.location ? (
                                <div className="location-info">
                                  <FiMapPin className="location-icon" />
                                  <span>
                                    {lead.location.city && lead.location.country
                                      ? `${lead.location.city}, ${lead.location.country}`
                                      : lead.location.country || lead.location.city || t('dashboard.agentleads.notAvailable')
                                    }
                                  </span>
                                </div>
                              ) : (
                                <span className="no-data">{t('dashboard.agentleads.notAvailable')}</span>
                              )}
                            </td>
                            <td data-label={t('dashboard.agentleads.sourceHeader')}>
                              {lead.source ? (
                                <div className="source-info">
                                  <span className={`source-badge source-${lead.source.toLowerCase()}`}>
                                    {lead.source}
                                  </span>
                                </div>
                              ) : (
                                <span className="no-data">{t('dashboard.agentleads.notAvailable')}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {pagination.total_pages > 1 && (
                    <div className="pagination-container">
                      <Pagination className="pagination-controls">
                        <Pagination.First
                          onClick={() => handlePageChange(1)}
                          disabled={!pagination.has_prev}
                        />
                        <Pagination.Prev
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={!pagination.has_prev}
                        />

                        {[...Array(pagination.total_pages)].map((_, index) => {
                          const page = index + 1;
                          const isVisible =
                            page === 1 ||
                            page === pagination.total_pages ||
                            (page >= currentPage - 2 && page <= currentPage + 2);

                          if (!isVisible) return null;

                          return (
                            <Pagination.Item
                              key={page}
                              active={page === currentPage}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </Pagination.Item>
                          );
                        })}

                        <Pagination.Next
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={!pagination.has_next}
                        />
                        <Pagination.Last
                          onClick={() => handlePageChange(pagination.total_pages)}
                          disabled={!pagination.has_next}
                        />
                      </Pagination>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state-container">
                  <div className="empty-state-icon">
                    <FiUsers size={48} />
                  </div>
                  <h4>{t('dashboard.agentleads.noLeadsFoundTitle')}</h4>
                  <p>
                    {appliedFilters.searchTerm || appliedFilters.startDate || appliedFilters.endDate
                      ? t('dashboard.agentleads.noLeadsFilteredMessage')
                      : t('dashboard.agentleads.noLeadsEmptyMessage')
                    }
                  </p>

                  <div className="empty-state-actions">
                    {(appliedFilters.searchTerm || appliedFilters.startDate || appliedFilters.endDate) && (
                      <OutlinedSecondaryButton
                        width="100%"
                        onClick={clearFilters}
                      >
                        <FiFilter className="me-2" />
                        {t('dashboard.agentleads.clearFiltersButton')}
                      </OutlinedSecondaryButton>
                    )}
                    <OutlinedSecondaryButton
                      width="100%"
                      onClick={() => fetchLeads(currentPage)}
                    >
                      <FiRefreshCw className="me-2" />
                      {t('dashboard.agentleads.refreshButton')}
                    </OutlinedSecondaryButton>

                    {/* Show tutorial buttons only when no filters are applied */}
                    {!appliedFilters.searchTerm && !appliedFilters.startDate && !appliedFilters.endDate && (
                      <>
                        <OutlinedPrimaryButton
                          width="100%"
                          onClick={handleRestartTour}
                        >
                          <FiInfo className="me-2" />
                          {t('dashboard.agentleads.viewTutorialButton')}
                        </OutlinedPrimaryButton>
                        <PrimaryButton
                          width="100%"
                          onClick={() => navigate(`/agent/${chatbotId}/action-flow`)}
                        >
                          {t('dashboard.agentleads.getStartedButton')}
                          <FiArrowRight className="ms-2" />
                        </PrimaryButton>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DatesProvider>
    </MantineProvider>
  );
};

export default AgentLeads;